import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Spinner } from "react-bootstrap";
import { fetchAds, updateAd, deleteAd, createAd } from "../../actions/advertsActions.js";
import { UPDATE_AD_METRICS } from "../../constants/advertConstants";
import { socket } from "../../utilities/socket";
import { MARKETS, DEFAULT_COUNTRY } from "../../config/markets.js";
import AdSimulation from "./AdSimulation.js";
import AdForm from "./AdForm.js";
import PlacementCell, { AdCenterStyles } from "./AdPlacementCell.js";
import {
  C, LINK_META, SLOT_META,
  desktopRenderUrl, describeLinkState, splitAdArtwork,
} from "./adCenterMeta.js";
import {
  FiPlus, FiEdit2, FiTrash2, FiRefreshCw,
  FiEye, FiMousePointer, FiClock, FiPlay,
  FiImage, FiToggleLeft, FiToggleRight, FiX,
} from "react-icons/fi";

// ── Publish status ────────────────────────────────────────────────────────────
// Derived from isActive + the scheduling window, matching EXACTLY the filter
// the public slot endpoint applies (getAdvertisementsBySlot) — so "Live" here
// means "customers are seeing this right now".
const computeAdStatus = (ad, nowMs = Date.now()) => {
  if (!ad?.isActive) return "paused";
  if (ad.startsAt && new Date(ad.startsAt).getTime() > nowMs) return "scheduled";
  if (ad.endsAt && new Date(ad.endsAt).getTime() < nowMs) return "expired";
  return "live";
};

const STATUS_META = {
  live:      { label: "Live",      color: "#00b69b", bg: "#e6faf7" },
  paused:    { label: "Paused",    color: "#8898aa", bg: "#f0f2f5" },
  scheduled: { label: "Scheduled", color: "#2c7be5", bg: "#eaf2ff" },
  expired:   { label: "Expired",   color: "#e63757", bg: "#fff0f3" },
};

// "3d 4h" / "2h 10m" / "5m" — how long the banner has been in front of
// customers. Anchored on the latest of lastPublishedAt (isActive flip) and
// startsAt (scheduled go-live), falling back to createdAt for legacy rows
// that predate publish-lifecycle stamping.
const liveForLabel = (ad, nowMs = Date.now()) => {
  if (computeAdStatus(ad, nowMs) !== "live") return null;
  const anchors = [ad.lastPublishedAt, ad.publishedAt, ad.startsAt, ad.createdAt]
    .map((d) => (d ? new Date(d).getTime() : NaN))
    .filter((t) => Number.isFinite(t) && t <= nowMs);
  if (!anchors.length) return null;
  const sinceMs = nowMs - Math.max(...anchors);
  const m = Math.floor(sinceMs / 60000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
};

const ctrLabel = (ad) => {
  const impressions = Number(ad?.impressions) || 0;
  const clicks = Number(ad?.clicks) || 0;
  if (!impressions) return clicks ? "—" : "0%";
  return `${((clicks / impressions) * 100).toFixed(1)}%`;
};

const compactCount = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  return String(v);
};

function Pill({ meta }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "3px 9px", borderRadius: 20,
      fontSize: 11, fontWeight: 700, letterSpacing: "0.04em",
      background: meta.bg, color: meta.color,
    }}>
      {meta.icon || null}
      {meta.label}
    </span>
  );
}

// ── Main AdvertManager ────────────────────────────────────────────────────────
const AdvertManager = () => {
  const dispatch = useDispatch();
  const ads     = useSelector((state) => state.adverts?.ads);
  const loading = useSelector((state) => state.adverts?.loading);

  const [showModal,      setShowModal]      = useState(false);
  const [currentAd,      setCurrentAd]      = useState(null);
  const [showDeleteModal,setShowDeleteModal] = useState(false);
  const [adToDelete,     setAdToDelete]      = useState(null);
  const [deleting,       setDeleting]        = useState(false);
  // Crop modal state lives here so it renders outside the AdForm modal (avoids nested Modal)
  const [showCropModal,  setShowCropModal]   = useState(false);
  const [previewImage,   setPreviewImage]    = useState(null); // { url, title } for lightbox
  const [simulateAd,     setSimulateAd]      = useState(null); // ad row → simulation modal

  useEffect(() => { dispatch(fetchAds()); }, [dispatch]);

  // Realtime — every advert mutation (any admin, any device) broadcasts
  // `advert:changed`; refetching keeps this table, the metrics, and the
  // status chips authoritative without manual refresh. The same event drives
  // the customer-facing carousels, so what admins see here is exactly as
  // fresh as what customers see.
  //
  // `advert:metrics` streams live impression/click counters (server-throttled
  // to one emit per ad per few seconds) into the superadminRoom — the same
  // admin-role-guarded room the GA4 realtime widget uses. We merge counts
  // into the matching row only, so the Performance column ticks up live
  // without refetches or loading flicker.
  useEffect(() => {
    const onAdvertChanged = () => dispatch(fetchAds());
    const onAdvertMetrics = (payload = {}) =>
      dispatch({ type: UPDATE_AD_METRICS, payload });
    const joinRoom = () => socket.emit("joinSuperadminRoom");

    socket.on("advert:changed", onAdvertChanged);
    socket.on("advert:metrics", onAdvertMetrics);
    socket.on("connect", joinRoom); // re-join after any reconnect
    if (socket.connected) joinRoom();

    return () => {
      socket.off("advert:changed", onAdvertChanged);
      socket.off("advert:metrics", onAdvertMetrics);
      socket.off("connect", joinRoom);
      socket.emit("leaveSuperadminRoom");
    };
  }, [dispatch]);

  // Re-render every 60s so "Live for" durations and Scheduled→Live→Expired
  // transitions advance without any user interaction.
  const [, setClockTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setClockTick((t) => t + 1), 60_000);
    return () => clearInterval(timer);
  }, []);

  // Success/error toasts are emitted by the action creators themselves once the
  // server actually responds — don't toast optimistically here or we'll show
  // "created" even when the POST 500s and the user is left thinking it worked.
  // The modal closes only on a successful save. Before this it closed the
  // instant the request was sent, so a 422 from the artwork gate left the
  // person with a toast and an empty table, and every upload to redo.
  const handleCreateAd = async (adData) => {
    const result = await dispatch(createAd(adData));
    if (result?.ok) setShowModal(false);
    return result;
  };

  const handleUpdateAd = async (adData) => {
    const result = await dispatch(updateAd(currentAd._id, adData));
    if (result?.ok) {
      setShowModal(false);
      setCurrentAd(null);
    }
    return result;
  };

  // Toggle isActive with a MINIMAL patch. Previously this spread the entire
  // ad document (`{ ...ad, isActive: !ad.isActive }`) which forced the
  // server to re-run slot logic, regenerate `imageUrlWebp`, and re-validate
  // every field — surfacing 500s for legacy rows whose `linkParams` or
  // `imageUrl` shape doesn't survive a round-trip through `runValidators`.
  // Sending just `{ isActive }` is faster AND avoids those side effects.
  const handleToggleActive = (ad) => {
    dispatch(updateAd(ad._id, { isActive: !ad.isActive }));
  };

  const handleDeleteAd = (ad) => { setAdToDelete(ad); setShowDeleteModal(true); };

  const confirmDelete = async () => {
    if (!adToDelete) return;
    setDeleting(true);
    try {
      dispatch(deleteAd(adToDelete._id));
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
      setAdToDelete(null);
    }
  };

  const openModal = (ad = null) => { setCurrentAd(ad); setShowModal(true); };
  const closeModal = () => { setShowModal(false); setCurrentAd(null); setShowCropModal(false); };

  const th = {
    padding: "10px 14px", textAlign: "left",
    fontSize: 11, fontWeight: 700, letterSpacing: "0.08em",
    textTransform: "uppercase", color: C.textLight,
    borderBottom: `2px solid ${C.border}`, whiteSpace: "nowrap",
    background: C.panel,
  };
  const td = {
    padding: "12px 14px", borderBottom: `1px solid ${C.border}`,
    color: C.textMid, verticalAlign: "middle", fontSize: 13,
  };

  return (
    <div style={{ background: C.bg, minHeight: "100vh", padding: "28px 16px 48px" }}>
      <AdCenterStyles />
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>

        {/* ── Page header ── */}
        <div style={{
          background: `linear-gradient(135deg, ${C.header} 0%, #243b6e 100%)`,
          borderRadius: C.radius, padding: "24px 28px", marginBottom: 24,
          boxShadow: "0 4px 24px rgba(26,43,74,0.18)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#fff", letterSpacing: "-0.3px", marginBottom: 3 }}>
              Ad Center
            </div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.65)" }}>
              Manage banners and promotional advertisements across all devices
            </div>
          </div>
          <button
            onClick={() => openModal()}
            style={{
              background: C.blue, border: "none", borderRadius: C.radiusSm,
              padding: "9px 18px", fontSize: 13, fontWeight: 700, color: "#fff",
              cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 7,
            }}
          >
            <FiPlus size={14} /> New Advertisement
          </button>
        </div>

        {/* ── Table panel ── */}
        <div style={{
          background: C.panel, borderRadius: C.radius,
          boxShadow: C.shadow, border: `1px solid ${C.border}`, overflow: "hidden",
        }}>
          {loading ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 56, color: C.textLight, gap: 10 }}>
              <Spinner animation="border" size="sm" style={{ color: C.blue, width: 16, height: 16 }} />
              <span style={{ fontSize: 13 }}>Loading advertisements...</span>
            </div>
          ) : !ads?.length ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "56px 24px", color: C.textLight }}>
              <FiImage size={40} style={{ marginBottom: 14, opacity: 0.35 }} />
              <div style={{ fontSize: 14, fontWeight: 700, color: C.textMid, marginBottom: 6 }}>No advertisements yet</div>
              <div style={{ fontSize: 12, marginBottom: 20 }}>Create your first banner to start promoting.</div>
              <button
                onClick={() => openModal()}
                style={{
                  background: C.blue, border: "none", borderRadius: C.radiusSm,
                  padding: "8px 18px", fontSize: 13, fontWeight: 700, color: "#fff",
                  cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
                }}
              >
                <FiPlus size={13} /> New Advertisement
              </button>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={th}>#</th>
                    <th style={th}>Preview</th>
                    <th style={th}>Title</th>
                    <th style={th}>Placement</th>
                    <th style={th}>Market</th>
                    <th style={th}>Link</th>
                    <th style={th}>Status</th>
                    <th style={th}>Performance</th>
                    <th style={th}>Order</th>
                    <th style={th}>Active</th>
                    <th style={{ ...th, textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(ads || []).map((ad, index) => {
                    const linkMeta   = LINK_META[ad.linkType]     || LINK_META.none;
                    // Plain-language destination, with an explicit warning
                    // when the link cannot open anything (legacy types, or
                    // product/shop rows saved before links were validated).
                    const linkState  = describeLinkState(ad);

                    return (
                      <tr key={ad._id} style={{ transition: "background 0.1s" }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = C.bg)}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        <td style={{ ...td, color: C.textLight, width: 36 }}>{index + 1}</td>

                        {/* Thumbnails, one per artwork the row carries. The
                            RAW urls, never the webp variants: those are
                            c_fill crops, so a wide strip would show as a
                            zoomed centre-crop here. objectFit "contain"
                            letterboxes but never crops. Click for full size. */}
                        <td style={{ ...td, width: 140 }}>
                          {(() => {
                            const art = splitAdArtwork(ad);
                            const thumbs = [];
                            if (art.imageUrl) thumbs.push({ key: "phone", url: art.imageUrl, aspect: SLOT_META[ad.slot]?.aspectRatio || 16 / 9, label: "Phone artwork" });
                            if (art.desktopImageUrl) thumbs.push({ key: "desktop", url: art.desktopImageUrl, aspect: 3159 / 498, label: "Desktop web artwork" });
                            if (thumbs.length === 0) {
                              return (
                                <div style={{ width: 48, height: 36, borderRadius: C.radiusSm, background: "#f0f2f5", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <FiImage size={14} style={{ color: C.textLight }} />
                                </div>
                              );
                            }
                            return (
                              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                {thumbs.map((t) => (
                                  <div key={t.key}>
                                    <img
                                      src={t.url}
                                      alt={`${ad.title} (${t.label})`}
                                      title={`${t.label}. Click to view full size`}
                                      onClick={() => setPreviewImage({ url: t.url, title: `${ad.title} (${t.label})` })}
                                      style={{
                                        width: t.key === "desktop" ? 132 : 88,
                                        aspectRatio: `${t.aspect}`,
                                        objectFit: "contain",
                                        background: "#f0f2f5",
                                        borderRadius: C.radiusSm,
                                        display: "block",
                                        border: `1px solid ${C.border}`,
                                        cursor: "zoom-in",
                                      }}
                                    />
                                    <div style={{ fontSize: 9.5, color: C.textLight, marginTop: 2, letterSpacing: "0.04em", textTransform: "uppercase" }}>
                                      {t.key === "desktop" ? "Desktop" : "Phone"}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            );
                          })()}
                        </td>

                        {/* Title + description */}
                        <td style={td}>
                          <div style={{ fontWeight: 700, color: C.text, marginBottom: 2 }}>{ad.title}</div>
                          {ad.description && (
                            <div style={{ fontSize: 11, color: C.textLight, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {ad.description}
                            </div>
                          )}
                        </td>

                        {/* Placement: where THIS row shows, with which artwork,
                            and a click-to-open details panel. */}
                        <td style={{ ...td, verticalAlign: "top", paddingTop: 14 }}>
                          <PlacementCell ad={ad} />
                        </td>

                        {/* Market of association (legacy null-country rows are UG) */}
                        <td style={td} title={MARKETS[ad.country || DEFAULT_COUNTRY]?.name || ""}>
                          <span style={{
                            display: "inline-flex", alignItems: "center",
                            padding: "3px 9px", borderRadius: 20,
                            fontSize: 11, fontWeight: 700, letterSpacing: "0.04em",
                            background: "#f0f2f5", color: C.text,
                          }}>
                            {ad.country || DEFAULT_COUNTRY}
                          </span>
                        </td>

                        {/* Link info */}
                        <td style={{ ...td, maxWidth: 190 }} title={linkState.detail}>
                          <Pill meta={linkMeta} />
                          <div style={{
                            fontSize: 11, marginTop: 3, lineHeight: 1.4,
                            color: linkState.tone === "warn" ? C.orange : linkState.tone === "ok" ? C.textMid : C.textLight,
                            fontWeight: linkState.tone === "warn" ? 600 : 400,
                            overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box",
                            WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                          }}>
                            {linkState.label}
                          </div>
                        </td>

                        {/* Publish status + live duration */}
                        <td style={td}>
                          {(() => {
                            const status = computeAdStatus(ad);
                            const meta = STATUS_META[status];
                            const duration = liveForLabel(ad);
                            return (
                              <>
                                <Pill meta={meta} />
                                {duration && (
                                  <div style={{ fontSize: 11, color: C.textLight, marginTop: 3, display: "flex", alignItems: "center", gap: 3 }}>
                                    <FiClock size={10} /> {duration}
                                  </div>
                                )}
                                {status === "scheduled" && ad.startsAt && (
                                  <div style={{ fontSize: 10, color: C.textLight, marginTop: 3 }}>
                                    from {new Date(ad.startsAt).toLocaleDateString()}
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </td>

                        {/* Performance — impressions · clicks · CTR */}
                        <td style={{ ...td, whiteSpace: "nowrap" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 5, fontVariantNumeric: "tabular-nums" }}>
                            <FiEye size={11} style={{ color: C.textLight }} />
                            <span style={{ fontWeight: 600, color: C.text }}>{compactCount(ad.impressions)}</span>
                            <FiMousePointer size={11} style={{ color: C.textLight, marginLeft: 6 }} />
                            <span style={{ fontWeight: 600, color: C.text }}>{compactCount(ad.clicks)}</span>
                          </div>
                          <div style={{ fontSize: 10, color: C.textLight, marginTop: 3 }}>
                            CTR {ctrLabel(ad)}
                          </div>
                        </td>

                        {/* Display order */}
                        <td style={{ ...td, textAlign: "center", width: 60 }}>
                          <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600, color: C.text }}>{ad.order ?? 0}</span>
                        </td>

                        {/* Inline active toggle */}
                        <td style={{ ...td, width: 64 }}>
                          <button
                            type="button"
                            onClick={() => handleToggleActive(ad)}
                            title={ad.isActive ? "Click to deactivate" : "Click to activate"}
                            style={{ background: "transparent", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center" }}
                          >
                            {ad.isActive
                              ? <FiToggleRight size={26} style={{ color: C.teal }} />
                              : <FiToggleLeft  size={26} style={{ color: C.textLight }} />}
                          </button>
                        </td>

                        {/* Actions */}
                        <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                          <button
                            onClick={() => setSimulateAd(ad)}
                            title="Preview on mobile & web"
                            style={{
                              background: C.purpleSoft, color: C.purple, border: "none",
                              borderRadius: C.radiusSm, padding: "5px 12px",
                              fontSize: 12, fontWeight: 600, cursor: "pointer",
                              display: "inline-flex", alignItems: "center", gap: 5, marginRight: 6,
                            }}
                          >
                            <FiPlay size={11} /> Simulate
                          </button>
                          <button
                            onClick={() => openModal(ad)}
                            style={{
                              background: C.blueSoft, color: C.blue, border: "none",
                              borderRadius: C.radiusSm, padding: "5px 12px",
                              fontSize: 12, fontWeight: 600, cursor: "pointer",
                              display: "inline-flex", alignItems: "center", gap: 5, marginRight: 6,
                            }}
                          >
                            <FiEdit2 size={11} /> Edit
                          </button>
                          <button
                            onClick={() => handleDeleteAd(ad)}
                            style={{
                              background: C.redSoft, color: C.red, border: "none",
                              borderRadius: C.radiusSm, padding: "5px 12px",
                              fontSize: 12, fontWeight: 600, cursor: "pointer",
                              display: "inline-flex", alignItems: "center", gap: 5,
                            }}
                          >
                            <FiTrash2 size={11} /> Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Table footer */}
          {ads?.length > 0 && (
            <div style={{
              borderTop: `1px solid ${C.border}`, padding: "10px 16px",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              fontSize: 11, color: C.textLight,
            }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                {ads.length} advertisement{ads.length !== 1 ? "s" : ""}
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <span style={{ width: 6, height: 6, borderRadius: 3, background: C.teal }} />
                  realtime
                </span>
              </span>
              <button
                onClick={() => dispatch(fetchAds())}
                style={{ background: "transparent", border: "none", cursor: "pointer", color: C.textLight, display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11 }}
              >
                <FiRefreshCw size={11} /> Refresh
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Create / Edit modal ── */}
      {/* Crop modal (showCropModal) is rendered inside AdForm's <> fragment but OUTSIDE
          the <form> element so there is no Modal-inside-Form HTML nesting issue */}
      <Modal show={showModal} onHide={closeModal} size="lg" scrollable>
        <Modal.Header closeButton style={{ borderBottom: `1px solid ${C.border}` }}>
          <Modal.Title>
            {currentAd ? "Edit Advertisement" : "Create Advertisement"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "24px 28px", background: C.bg }}>
          <AdForm
            ad={currentAd}
            onSubmit={currentAd ? handleUpdateAd : handleCreateAd}
            onHide={closeModal}
            showCropModal={showCropModal}
            setShowCropModal={setShowCropModal}
            siblings={ads || []}
          />
        </Modal.Body>
      </Modal>

      {/* ── Simulation modal — faithful mobile/web preview from the table ── */}
      <Modal show={!!simulateAd} onHide={() => setSimulateAd(null)} centered>
        <Modal.Header closeButton style={{ borderBottom: `1px solid ${C.border}` }}>
          <Modal.Title>
            Simulate — {simulateAd?.title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "20px 24px", background: C.bg }}>
          {simulateAd && (
            <AdSimulation
              imageUrl={splitAdArtwork(simulateAd).imageUrl}
              imageUrlWebp={simulateAd.deviceType === "desktop" ? "" : simulateAd.imageUrlWebp}
              desktopImageUrl={desktopRenderUrl(simulateAd)}
              slot={simulateAd.slot}
              title={simulateAd.title}
              description={simulateAd.description}
              deviceType={simulateAd.deviceType}
              linkType={simulateAd.linkType}
              buttonText={simulateAd.buttonText}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* ── Image lightbox ── */}
      {previewImage && (
        <div
          onClick={() => setPreviewImage(null)}
          style={{
            position: "fixed", inset: 0, zIndex: 9999,
            background: "rgba(0,0,0,0.82)",
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            cursor: "zoom-out",
          }}
        >
          <div style={{ position: "relative", maxWidth: "90vw", maxHeight: "85vh" }}>
            <img
              src={previewImage.url}
              alt={previewImage.title}
              style={{
                maxWidth: "90vw", maxHeight: "80vh",
                borderRadius: 10, boxShadow: "0 8px 40px rgba(0,0,0,0.6)",
                display: "block",
              }}
              onClick={(e) => e.stopPropagation()}
            />
            <div style={{
              marginTop: 12, textAlign: "center",
              color: "rgba(255,255,255,0.8)", fontSize: 13, fontWeight: 600,
            }}>
              {previewImage.title}
            </div>
            <button
              onClick={() => setPreviewImage(null)}
              style={{
                position: "absolute", top: -14, right: -14,
                width: 32, height: 32, borderRadius: "50%",
                background: "#fff", border: "none", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
              }}
            >
              <FiX size={16} style={{ color: "#111" }} />
            </button>
          </div>
          <div style={{ marginTop: 10, fontSize: 11, color: "rgba(255,255,255,0.4)" }}>Click anywhere to close</div>
        </div>
      )}

      {/* ── Delete confirmation modal ── */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton style={{ borderBottom: `1px solid ${C.border}` }}>
          <Modal.Title>
            Delete Advertisement
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "20px 24px" }}>
          <p style={{ fontSize: 14, color: C.textMid, marginBottom: 6 }}>
            Are you sure you want to delete <strong style={{ color: C.text }}>{adToDelete?.title}</strong>?
          </p>
          <p style={{ fontSize: 12, color: C.textLight, marginBottom: 0 }}>This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: `1px solid ${C.border}` }}>
          <button
            type="button"
            onClick={() => setShowDeleteModal(false)}
            style={{
              background: "transparent", border: `1px solid ${C.border}`,
              borderRadius: C.radiusSm, padding: "8px 16px",
              fontSize: 13, fontWeight: 600, color: C.textMid, cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirmDelete}
            disabled={deleting}
            style={{
              background: deleting ? "#c8d6ea" : C.red, border: "none",
              borderRadius: C.radiusSm, padding: "8px 20px",
              fontSize: 13, fontWeight: 700, color: "#fff",
              cursor: deleting ? "not-allowed" : "pointer",
              display: "inline-flex", alignItems: "center", gap: 8,
            }}
          >
            {deleting
              ? <><Spinner animation="border" size="sm" style={{ width: 13, height: 13 }} /> Deleting...</>
              : <><FiTrash2 size={13} /> Delete</>}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdvertManager;
